import { useEffect, useState } from 'react';
import { useNavigate } from '@usas/ui-web/wrappers/ReactRouterDom';

import NavLinks from '../../views/meet/NavLinks';
import MyMeetsNavLinks from '../../views/myMeets/MyMeetsNavLinks';
import MyAthleteEntryNavLinks from '../../views/myAthleteEntry/MyAthleteEntryNavLinks';
import AthleteUserViews from '../../views/entry/athleteUser/AthleteUserViews';
import { getViewData } from '../../views/entry/athleteUser/AthleteUserViews';

import useOmeMeetData from '../../state/omeMeet/UseOmeMeetData';
import useOrgUnitAthleteEntryInfoData from '../../state/orgUnitAthleteEntryInfo/UseOrgUnitAthleteEntryInfoData';
import useOmeMeetOrgUnitAthleteEntryIndividualRosterEntryData from '../../state/omeMeetOrgUnitAthleteEntryIndividualRosterEntry/UseOmeMeetOrgUnitAthleteEntryIndividualRosterEntryData';
import useOmeMeetOrgUnitEntryData from '../../state/omeMeetOrgUnitEntry/UseOmeMeetOrgUnitEntryData';
import useAthleteEntryContextData from '../../state/athleteEntryContextView/UseAthleteEntryContextData';
import useMyAthleteMeetEntriesFiltersData from '../../state/myAthleteMeetEntriesFilters/UseMyAthleteMeetEntriesFiltersData';

import useSearchMeetData from '@usas/ui-web/state/searchMeet/UseSearchMeetData';
import useSelectOrgUnitData from '@usas/ui-web/state/selectOrgUnit/UseSelectOrgUnitData';

const INITIAL_STATE = {
  name: '',
  subHeaderText: '',
  instructionText: '',
  nextButtonText: '',
  backButtonText: '',
  saveAndExitButtonText: ''
};

const useViewTemplate = (viewName, onBackButtonClicked) => {
  const navigate = useNavigate();
  const { omeMeetState } = useOmeMeetData();
  const { clearOmeMeetOrgUnitEntryObjData } = useOmeMeetOrgUnitEntryData();
  const { orgUnitAthleteEntryInfoState, clearOrgUnitAthleteEntryInfoObjData } = useOrgUnitAthleteEntryInfoData();
  const { omeMeetOrgUnitAthleteEntryIndividualRosterEntryState, clearOmeMeetOrgUnitAthleteEntryIndividualRosterEntryObjData }
    = useOmeMeetOrgUnitAthleteEntryIndividualRosterEntryData();
  const { searchMeetState, setSearchMeetState } = useSearchMeetData();
  const { selectOrgUnitState } = useSelectOrgUnitData();
  const { athleteEntryContextState } = useAthleteEntryContextData();
  const { myAthleteMeetEntriesFiltersState } = useMyAthleteMeetEntriesFiltersData();
  const [view, setView] = useState(INITIAL_STATE);

  const handleBackButtonClicked = (e) => {
    if (onBackButtonClicked instanceof Function) {
      onBackButtonClicked(e);
    } else {
      if (e?.preventDefault) {
        e.preventDefault();
      }

      clearOmeMeetOrgUnitEntryObjData();
      clearOrgUnitAthleteEntryInfoObjData();
      clearOmeMeetOrgUnitAthleteEntryIndividualRosterEntryObjData();

      if (omeMeetState.route === '') {
        if (selectOrgUnitState.orgUnitId === '') {
          //My Athlete Meet Entries Route
          navigate(MyAthleteEntryNavLinks.MY_ATHLETE_ENTRY, { state: { myAthleteMeetEntriesFiltersState: myAthleteMeetEntriesFiltersState } });
        }
        else {
          //OME Route
          navigate(MyMeetsNavLinks.MEET_ENTRY_ATHLETE, { state: { selectOrgUnitState: selectOrgUnitState } });
        }
      }
      else {
        //Meet Route
        setSearchMeetState({ ...searchMeetState, showMeetHeader: true });

        navigate(NavLinks.MEET_ENTRY_ATHLETE, { state: { goToMeet: true } });
      }
    }
  };

  useEffect(() => {
    const foundView = getViewData(viewName);
    if (foundView) {
      if (athleteEntryContextState.isReadOnly === true) {
        foundView.saveAndExitButtonText = 'Exit';
        if (foundView.name === AthleteUserViews.OME_MEET_SUBMITTED) {
          foundView.nextButtonText = '';
        }
      }

      if (foundView.name === AthleteUserViews.OME_MEET_CONTACTS) {
        if (athleteEntryContextState.isUnattached === false) {
          foundView.instructionText = 'Only Unattached Athletes can add an Entry Contact. Teams will add Entry Contacts through the Team Entry process.'
        }
        else {
          foundView.instructionText = "Provide at least one Entry Contact.";
        }
      }
      if (foundView.name === AthleteUserViews.OME_MEET_ROSTER_ENTRIES_DETAIL) {
        if (omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.objData.status === 'Relay Only Swimmer') {
          foundView.instructionText = 'Relay Only Swimmers cannot enter Individual Events. Teams will add Relay Entries through the Team Entry process.'
        }
        else {
          foundView.instructionText = "Add or Remove Event Entries.";
        }
      }
      if (foundView.name === AthleteUserViews.OME_MEET_CREDENTIALS) {
        if (athleteEntryContextState.isUnattached === false) {
          foundView.instructionText = 'Only Unattached Athletes can add a Credential. Teams will add Credentials through the Team Entry process.'
        }
        else {
          foundView.instructionText = orgUnitAthleteEntryInfoState.objData?.maxCredentialNumber !== undefined && orgUnitAthleteEntryInfoState.objData?.maxCredentialNumber !== null && orgUnitAthleteEntryInfoState.objData?.maxCredentialNumber >= 0 ? `Add or Remove Credentials. The Number of Allowed Credentials is Determined by the Meet's Deck Pass Rules. Currently, ${orgUnitAthleteEntryInfoState.objData?.maxCredentialNumber ?? ''} Credential${orgUnitAthleteEntryInfoState.objData?.maxCredentialNumber === 1 ? ' is' : 's are'} Allowed. ${orgUnitAthleteEntryInfoState.objData?.credentialsCanBeAddedAfterCap === true ? 'Only Massage Credentials can be Added after the Cap has been Reached.' : 'No Credentials can be Added after the Cap has been Reached.'}` :
            "Add or Remove Credentials. The Number of Allowed Credentials is Determined by the Meet's Deck Pass Rules.";
        }
      }
      if (foundView.name === AthleteUserViews.OME_MEET_SUBMIT || foundView.name === AthleteUserViews.OME_MEET_SUBMIT) {
        if (athleteEntryContextState.isUnattached === false) {
          foundView.instructionText = 'Only Unattached Athletes can make purchases. Teams will complete any purchases associated with their athletes through the Team Entry process.';
        }
        else {
          foundView.instructionText = 'Make Purchases and Submit the Athlete Entry.';
        }
      }
      setView({ ...foundView });
    }
  }, [viewName, athleteEntryContextState.isReadOnly, athleteEntryContextState.isUnattached, orgUnitAthleteEntryInfoState, omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.objData]);

  return {
    ...view,
    handleBackButtonClicked,
    orgUnitAthleteEntryInfoState,
    omeMeetOrgUnitAthleteEntryIndividualRosterEntryState
  };
};

export default useViewTemplate;
